import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, MailCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ApiError, authApi } from "@/api/http";
import { Mark } from "@/components/Layout";
import { safeNext } from "@/lib/safeNext";

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-app place-items-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          <Mark />
          <div className="font-semibold">Кабинет клиента</div>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Where to go after signing in: the page the person came from, if it is ours. */
function nextPath() {
  return safeNext(new URLSearchParams(location.search).get("next") ?? "");
}

export function Login() {
  const [email, setEmail] = useState("");
  const send = useMutation({ mutationFn: authApi.login, meta: { inline: true } });
  // The dev sign-in list exists only while portal-bff runs with dev auth.
  const dev = useQuery({
    queryKey: ["dev-people-probe"],
    queryFn: async () => (await fetch("/api/dev/people")).ok,
    retry: false,
  });
  useEffect(() => {
    const n = nextPath();
    try {
      if (n) sessionStorage.setItem("portal.next", n);
    } catch {
      /* private mode: land on the default page */
    }
  }, []);

  if (send.isSuccess)
    return (
      <Card>
        <MailCheck className="size-6" />
        <h1 className="mt-4 text-lg font-semibold">Проверьте почту</h1>
        <p className="mt-2 text-sm text-muted">
          {send.data.detail} Ссылка действует 20 минут и открывает кабинет один
          раз.
        </p>
        {send.data.devLink && (
          <a
            className="mt-4 block break-all text-sm underline underline-offset-2"
            href={send.data.devLink}
          >
            Локальная разработка: войти по ссылке
          </a>
        )}
        <button className="btn-ghost mt-6 px-0" onClick={() => send.reset()}>
          Указать другой адрес
        </button>
      </Card>
    );

  return (
    <Card>
      <h1 className="text-lg font-semibold">Вход</h1>
      <p className="mt-1 text-sm text-muted">
        Пришлём ссылку для входа на почту, которую вы дали студии. Пароль не
        нужен.
      </p>
      <form
        className="mt-6 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) send.mutate(email.trim());
        }}
      >
        <label className="block text-sm font-medium" htmlFor="email">
          Почта
        </label>
        <input
          id="email"
          type="email"
          required
          autoComplete="email"
          autoFocus
          className="field h-10 w-full"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {send.error && (
          <p className="text-sm text-overdue">
            {send.error instanceof ApiError && send.error.status === 429
              ? "Слишком много попыток входа. Попробуйте через минуту."
              : (send.error as Error).message}
          </p>
        )}
        <button className="btn-primary h-10 w-full" disabled={send.isPending}>
          {send.isPending && <Loader2 className="size-4 animate-spin" />}
          Прислать ссылку
        </button>
      </form>
      {dev.data && (
        <a
          className="mt-6 block text-center text-xs text-muted underline underline-offset-2"
          href="/dev-signin"
        >
          Локальная разработка: выбрать пользователя
        </a>
      )}
    </Card>
  );
}

export function AuthCallback() {
  const [error, setError] = useState<string>();
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // The token travels in the fragment: it never reaches server logs or a Referer.
    const token = new URLSearchParams(location.hash.slice(1)).get("token");
    history.replaceState(null, "", location.pathname);
    if (!token) {
      setError("В ссылке нет ключа входа. Откройте ссылку из письма целиком.");
      return;
    }
    authApi.callback(token).then(
      (r) => {
        let next = "";
        try {
          next = safeNext(sessionStorage.getItem("portal.next") ?? "");
          sessionStorage.removeItem("portal.next");
        } catch {
          /* ignore */
        }
        location.replace(next || (r.side === "studio" ? "/studio" : "/"));
      },
      (e: Error) => setError(e.message),
    );
  }, []);
  return (
    <Card>
      {error ? (
        <>
          <h1 className="text-lg font-semibold">Не получилось войти</h1>
          <p className="mt-2 text-sm text-muted">{error}</p>
          <a className="btn-primary mt-6 h-10 w-full" href="/login">
            Запросить новую ссылку
          </a>
        </>
      ) : (
        <p className="flex items-center gap-2 text-sm text-muted">
          <Loader2 className="size-4 animate-spin" /> Входим…
        </p>
      )}
    </Card>
  );
}
